import React, { useEffect, useMemo, useState } from "react";
import { Ticket, Plus, Trash2, CheckCircle2, Clock, Ban, Search, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { fmtDate, fmtDateTime } from "../../lib/time";
import { useAuth } from "../../context/AuthContext";
import { Label, EmptyState } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const TRIGGERS = [
  { key: "recovery",    label: "Service Recovery",     hint: "Issued when a recovery case is marked Resolved (customer accepted callback)." },
  { key: "high_rating", label: "5-star Rating",        hint: "Issued when a customer gives a 5-star rating." },
  { key: "returning",   label: "Returning Customer",   hint: "Issued on the Nth repeat scan by the same device." },
  { key: "campaign",    label: "Scheduled Campaign",   hint: "Issued to every rating during a date window." },
];
const TRIGGER_LABEL = Object.fromEntries(TRIGGERS.map((t) => [t.key, t.label]));

const STATUS_COLOR = { issued: "#4338CA", redeemed: "#10B981", expired: "#94A3B8", void: "#EF4444" };

const emptyRule = () => ({
  location_id: "", trigger: "recovery", enabled: true,
  title: "", offer: "", terms: "", validity_days: 30,
  visits_threshold: 3, campaign_from: "", campaign_to: "",
  delivery_email: true, delivery_sms: true,
});

export default function CustomerVouchers() {
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [rules, setRules] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [stats, setStats] = useState(null);
  const [openRule, setOpenRule] = useState(false);
  const [form, setForm] = useState(emptyRule());
  const [lookup, setLookup] = useState("");

  useEffect(() => {
    api.get("/locations").then((r) => {
      setLocations(r.data || []);
      if ((r.data || []).length) setLocationId(r.data[0].id);
    }).catch(() => {});
  }, []);

  const refresh = () => {
    if (!locationId) return;
    api.get(`/vouchers/rules?location_id=${locationId}`).then((r) => setRules(r.data || [])).catch(() => {});
    api.get(`/vouchers?location_id=${locationId}&limit=500`).then((r) => setVouchers(r.data || [])).catch(() => {});
    api.get(`/vouchers/stats?location_id=${locationId}`).then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(refresh, [locationId]); // eslint-disable-line

  const toggleEnabled = async (rule) => {
    try { await api.patch(`/vouchers/rules/${rule.id}`, { enabled: !rule.enabled }); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const removeRule = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.title}"? Issued vouchers are kept.`)) return;
    try { await api.delete(`/vouchers/rules/${rule.id}`); toast.success("Rule removed"); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const openCreate = () => { setForm({ ...emptyRule(), location_id: locationId }); setOpenRule(true); };

  const saveRule = async () => {
    try {
      await api.post("/vouchers/rules", { ...form, location_id: locationId });
      toast.success("Rule created"); setOpenRule(false); refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const redeem = async (v) => {
    try {
      await api.post(`/vouchers/${v.id}/redeem`, { code: v.code });
      toast.success(`Voucher ${v.code} redeemed`); refresh();
    } catch (e) { toast.error(apiError(e)); }
  };
  const voidV = async (v) => {
    if (!window.confirm(`Void voucher ${v.code}?`)) return;
    try { await api.post(`/vouchers/${v.id}/void`, {}); toast.success("Voucher voided"); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const lookupByCode = async () => {
    const code = lookup.trim().toUpperCase();
    if (!code) return;
    try {
      const { data } = await api.get(`/vouchers/by-code/${code}`);
      if (data.status === "issued") {
        if (window.confirm(`Found voucher: ${data.title} — ${data.offer}\n\nRedeem it now?`)) redeem(data);
      } else {
        toast.message(`Voucher ${code} is ${data.status}`);
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  const copy = (code) => { navigator.clipboard?.writeText(code).then(() => toast.success(`Copied ${code}`)); };

  const triggerColor = (t) => ({ recovery: "#F97316", high_rating: "#10B981", returning: "#4338CA", campaign: "#DB2777" }[t] || "#64748B");

  return (
    <div className="space-y-6" data-testid="customer-vouchers-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Label>Customer</Label>
          <h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Customer Vouchers</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">Reward customers automatically — after a 5-star rating, a resolved complaint, a returning visit, or during a campaign window. Codes land on their Thank-You screen and in their email/SMS.</p>
        </div>
        {locations.length > 1 && (
          <Select value={locationId} onValueChange={setLocationId}>
            <SelectTrigger className="w-[220px]" data-testid="location-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      {stats && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <StatTile label="Issued" value={stats.issued} tone="indigo" testid="stat-issued" />
          <StatTile label="Redeemed" value={stats.redeemed} tone="emerald" testid="stat-redeemed" />
          <StatTile label="Outstanding" value={stats.outstanding} tone="amber" testid="stat-outstanding" />
          <StatTile label="Expired" value={stats.expired} tone="rose" testid="stat-expired" />
          <StatTile label="Redemption rate" value={`${Math.round((stats.redemption_rate || 0) * 100)}%`} tone="slate" testid="stat-rate" />
        </div>
      )}

      <Tabs defaultValue="rules">
        <TabsList>
          <TabsTrigger value="rules" data-testid="tab-rules">Rules</TabsTrigger>
          <TabsTrigger value="vouchers" data-testid="tab-vouchers">Issued vouchers</TabsTrigger>
          <TabsTrigger value="redeem" data-testid="tab-redeem">Redeem a code</TabsTrigger>
        </TabsList>

        {/* RULES */}
        <TabsContent value="rules" className="mt-4">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm text-slate-500">Rules are per-location. Toggle them off to pause issuance.</p>
            <Button onClick={openCreate} data-testid="new-rule-btn"><Plus className="mr-1 h-4 w-4" /> New rule</Button>
          </div>
          {rules.length === 0 ? (
            <EmptyState icon={Ticket} title="No voucher rules yet" hint="Create one to start thanking customers automatically." />
          ) : (
            <div className="space-y-3">
              {rules.map((r) => (
                <div key={r.id} data-testid={`rule-${r.id}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white" style={{ background: triggerColor(r.trigger) }}>
                          {TRIGGER_LABEL[r.trigger] || r.trigger}
                        </span>
                        <h3 className="font-head text-lg font-bold text-slate-900">{r.title}</h3>
                      </div>
                      <p className="mt-1 text-sm text-slate-700">{r.offer}</p>
                      {r.terms && <p className="mt-0.5 text-xs text-slate-500">{r.terms}</p>}
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                        <span>Valid for {r.validity_days} days</span>
                        {r.trigger === "returning" && <span>After {r.visits_threshold} visits</span>}
                        {r.trigger === "campaign" && <span>{fmtDate(r.campaign_from)} – {fmtDate(r.campaign_to)}</span>}
                        <span>{r.delivery_email ? "Email ✓" : "Email ×"}</span>
                        <span>{r.delivery_sms ? "SMS ✓" : "SMS ×"}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">{r.enabled ? "On" : "Off"}</span>
                        <Switch checked={r.enabled} onCheckedChange={() => toggleEnabled(r)} data-testid={`toggle-${r.id}`} />
                      </div>
                      <button onClick={() => removeRule(r)} className="rounded-md p-2 text-slate-400 hover:bg-red-50 hover:text-red-600" data-testid={`delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* VOUCHERS */}
        <TabsContent value="vouchers" className="mt-4">
          {vouchers.length === 0 ? (
            <EmptyState icon={Ticket} title="No vouchers issued yet" hint="Vouchers appear here as customers trigger your rules." />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2">Code</th><th className="px-4 py-2">Trigger</th><th className="px-4 py-2">Offer</th>
                    <th className="px-4 py-2">Customer</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Expires</th>
                    <th className="px-4 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vouchers.map((v) => (
                    <tr key={v.id} className="border-t border-slate-100" data-testid={`voucher-${v.id}`}>
                      <td className="px-4 py-2 font-mono font-bold">
                        <button onClick={() => copy(v.code)} className="inline-flex items-center gap-1 hover:text-indigo-600" title="Copy code">
                          {v.code} <Copy className="h-3 w-3 opacity-50" />
                        </button>
                      </td>
                      <td className="px-4 py-2">
                        <span className="rounded-full px-2 py-0.5 text-[11px] font-bold text-white" style={{ background: triggerColor(v.trigger) }}>{TRIGGER_LABEL[v.trigger] || v.trigger}</span>
                      </td>
                      <td className="px-4 py-2 text-slate-700"><div className="font-semibold">{v.title}</div><div className="text-xs text-slate-500">{v.offer}</div></td>
                      <td className="px-4 py-2 text-slate-700">{v.contact_name || "—"}<div className="text-xs text-slate-500">{v.contact_phone || v.contact_email || ""}</div></td>
                      <td className="px-4 py-2">
                        <span className="rounded-full px-2 py-0.5 text-[11px] font-bold text-white" style={{ background: STATUS_COLOR[v.status] }}>{v.status}</span>
                      </td>
                      <td className="px-4 py-2 text-slate-500">{fmtDate(v.expires_at)}</td>
                      <td className="px-4 py-2 text-right">
                        {v.status === "issued" && (
                          <div className="flex justify-end gap-1">
                            <Button size="sm" onClick={() => redeem(v)} data-testid={`redeem-${v.id}`}><CheckCircle2 className="mr-1 h-3 w-3" />Redeem</Button>
                            <Button size="sm" variant="outline" onClick={() => voidV(v)} data-testid={`void-${v.id}`}><Ban className="h-3 w-3" /></Button>
                          </div>
                        )}
                        {v.status === "redeemed" && <span className="text-xs text-slate-400">{fmtDateTime(v.redeemed_at)}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* REDEEM */}
        <TabsContent value="redeem" className="mt-4">
          <div className="max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <Label>Staff redemption</Label>
            <h3 className="mt-1 font-head text-lg font-bold text-slate-900">Enter the customer's voucher code</h3>
            <p className="mt-1 text-xs text-slate-500">Type or paste the 6-character code the customer shows you. We'll check validity, show the offer and mark it as redeemed.</p>
            <div className="mt-4 flex gap-2">
              <Input value={lookup} onChange={(e) => setLookup(e.target.value.toUpperCase())}
                placeholder="e.g. B3S1RS" maxLength={12} className="font-mono uppercase tracking-widest"
                data-testid="lookup-input" />
              <Button onClick={lookupByCode} data-testid="lookup-btn"><Search className="mr-1 h-4 w-4" />Find</Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* CREATE RULE MODAL */}
      <Dialog open={openRule} onOpenChange={setOpenRule}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sv-scroll">
          <DialogHeader><DialogTitle>New voucher rule</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Trigger</Label>
              <Select value={form.trigger} onValueChange={(v) => setForm({ ...form, trigger: v })}>
                <SelectTrigger data-testid="form-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>{TRIGGERS.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
              <p className="mt-1 text-xs text-slate-500">{TRIGGERS.find((t) => t.key === form.trigger)?.hint}</p>
            </div>
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Thanks for the love!" data-testid="form-title" />
            </div>
            <div>
              <Label>Offer</Label>
              <Input value={form.offer} onChange={(e) => setForm({ ...form, offer: e.target.value })}
                placeholder="e.g. 10% off your next purchase" data-testid="form-offer" />
            </div>
            <div>
              <Label>Terms & conditions (optional)</Label>
              <Textarea value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })}
                placeholder="One-time use. Not redeemable for cash. Not combinable." className="min-h-[70px]" data-testid="form-terms" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Valid for (days)</Label>
                <Input type="number" min={1} max={365} value={form.validity_days}
                  onChange={(e) => setForm({ ...form, validity_days: parseInt(e.target.value) || 30 })}
                  data-testid="form-validity" />
              </div>
              {form.trigger === "returning" && (
                <div>
                  <Label>Issue after N visits</Label>
                  <Input type="number" min={2} max={50} value={form.visits_threshold}
                    onChange={(e) => setForm({ ...form, visits_threshold: parseInt(e.target.value) || 2 })}
                    data-testid="form-visits" />
                </div>
              )}
            </div>
            {form.trigger === "campaign" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>From</Label>
                  <Input type="date" value={form.campaign_from}
                    onChange={(e) => setForm({ ...form, campaign_from: e.target.value })} data-testid="form-from" />
                </div>
                <div>
                  <Label>To</Label>
                  <Input type="date" value={form.campaign_to}
                    onChange={(e) => setForm({ ...form, campaign_to: e.target.value })} data-testid="form-to" />
                </div>
              </div>
            )}
            <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
              <Label>Delivery</Label>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span>Send by email</span>
                <Switch checked={form.delivery_email} onCheckedChange={(v) => setForm({ ...form, delivery_email: v })} data-testid="form-email" />
              </div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span>Send by SMS</span>
                <Switch checked={form.delivery_sms} onCheckedChange={(v) => setForm({ ...form, delivery_sms: v })} data-testid="form-sms" />
              </div>
              <p className="mt-2 text-[11px] text-slate-500">The on-screen code is always shown on the Thank-You screen.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenRule(false)}>Cancel</Button>
            <Button onClick={saveRule} disabled={!form.title || !form.offer} data-testid="save-rule">Create rule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatTile({ label, value, tone, testid }) {
  const bg = { indigo: "bg-indigo-50 text-indigo-700", emerald: "bg-emerald-50 text-emerald-700",
               amber: "bg-amber-50 text-amber-700", rose: "bg-rose-50 text-rose-700",
               slate: "bg-slate-100 text-slate-700" }[tone];
  return (
    <div className={`rounded-xl ${bg} p-4 shadow-sm`} data-testid={testid}>
      <div className="text-xs font-bold uppercase tracking-wide opacity-70">{label}</div>
      <div className="mt-1 font-head text-3xl font-bold">{value}</div>
    </div>
  );
}
